import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle, Clock, Scale } from 'lucide-react';
import { Procurement } from '@/types/database.types';
import { format } from 'date-fns';
import { ItemQualityInspectionSection } from './ItemQualityInspectionSection';
import { ProcurementItem } from '@/types/database/procurement-items';

interface QualityInspectionSectionProps {
  procurement: Procurement;
}

interface QualityData {
  grading_type?: 'preliminary' | 'final' | string | null;
  compensation_kg?: number | null;
  preliminary_compensation_kg?: number | null;
  damage_kg?: number | null;
  preliminary_damage_kg?: number | null;
  damage_percentage?: number | null;
  preliminary_damage_percentage?: number | null;
  is_final_grading_complete?: boolean | null;
  final_grading_expected_date?: string | null;
  preliminary_graded_at?: string | null;
  final_graded_at?: string | null;
  quality_grade?: string | null;
  sample_size_checked?: boolean | null;
  sample_size_notes?: string | null;
  damage_notes?: string | null;
  preliminary_notes?: string | null;
}

// Statuses where quality data should already exist
const STATUSES_REQUIRING_QUALITY = [
  'pending_warehouse',
  'pending_finance', 
  'completed'
];

export const QualityInspectionSection: React.FC<QualityInspectionSectionProps> = ({ procurement }) => {
  const quality = procurement.quality as QualityData | undefined;

  if (!quality) {
    const shouldHaveQuality = STATUSES_REQUIRING_QUALITY.includes(procurement.status);
    
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Quality Inspection
            </CardTitle>
            {shouldHaveQuality && (
              <Badge variant="destructive">Missing Data</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {shouldHaveQuality ? (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Quality inspection data is missing</AlertTitle>
              <AlertDescription>
                This procurement has progressed past quality inspection but no inspection record was found. 
                The inspection step may have been skipped or data was not saved correctly.
              </AlertDescription>
            </Alert>
          ) : (
            <p className="text-muted-foreground">Quality inspection pending.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const isPreliminary = quality.grading_type === 'preliminary';
  const isFinalComplete = quality.is_final_grading_complete === true;
  
  // Check if quality inspection has actually been performed
  // grading_type being set doesn't mean inspection was done - need to check timestamps or completion flags
  const hasCompletedInspection = 
    (quality.preliminary_graded_at !== null && quality.preliminary_graded_at !== undefined) ||
    (quality.final_graded_at !== null && quality.final_graded_at !== undefined) ||
    isFinalComplete;
  
  // Get values based on grading type
  const preliminaryDamage = quality.preliminary_damage_kg || 0;
  const preliminaryCompensation = quality.preliminary_compensation_kg || 0;
  const preliminaryDamagePercent = quality.preliminary_damage_percentage || 0;
  
  const finalDamage = quality.damage_kg || 0;
  const finalCompensation = quality.compensation_kg || 0;
  const finalDamagePercent = quality.damage_percentage || 0;
  
  // Check for reconciliation needed
  const hasReconciliationNeeded = isPreliminary && 
    quality.preliminary_compensation_kg !== null && 
    quality.compensation_kg !== null &&
    quality.preliminary_compensation_kg !== quality.compensation_kg;

  // Get current compensation for display
  const currentCompensation = isPreliminary && !isFinalComplete ? preliminaryCompensation : finalCompensation;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Quality Inspection
          </CardTitle>
          <div className="flex items-center gap-2">
            {!hasCompletedInspection ? (
              <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                <Clock className="h-3 w-3 mr-1" />
                Pending Inspection
              </Badge>
            ) : isPreliminary && !isFinalComplete ? (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <Clock className="h-3 w-3 mr-1" />
                Preliminary Assessment
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle className="h-3 w-3 mr-1" />
                Final Grading Complete
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Assessment */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Damage (kg)</p>
            <p className="text-xl font-semibold">
              {isPreliminary && !isFinalComplete 
                ? preliminaryDamage.toFixed(2) 
                : finalDamage.toFixed(2)}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Damage %</p>
            <p className="text-xl font-semibold">
              {isPreliminary && !isFinalComplete 
                ? preliminaryDamagePercent.toFixed(2) 
                : finalDamagePercent.toFixed(2)}%
            </p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="text-sm text-muted-foreground">Compensation (kg)</p>
            <p className="text-xl font-semibold text-amber-700">{currentCompensation.toFixed(2)}</p>
          </div>
        </div>

        {/* Quality Grade */}
        {quality.quality_grade && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Quality Grade:</span>
            <Badge variant="secondary" className="text-sm">
              {quality.quality_grade}
            </Badge>
          </div>
        )}

        {/* Preliminary vs Final Comparison */}
        {isPreliminary && quality.compensation_kg !== null && quality.preliminary_compensation_kg !== null && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-4 py-2 font-medium text-sm">
              Preliminary vs Final Comparison
            </div>
            <div className="p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="text-left pb-2">Metric</th>
                    <th className="text-right pb-2">Preliminary</th>
                    <th className="text-right pb-2">Final</th>
                    <th className="text-right pb-2">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-1">Damage (kg)</td>
                    <td className="text-right">{preliminaryDamage.toFixed(2)}</td>
                    <td className="text-right">{finalDamage.toFixed(2)}</td>
                    <td className="text-right font-medium">
                      {(finalDamage - preliminaryDamage).toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1">Damage %</td>
                    <td className="text-right">{preliminaryDamagePercent.toFixed(2)}%</td>
                    <td className="text-right">{finalDamagePercent.toFixed(2)}%</td>
                    <td className="text-right font-medium">
                      {(finalDamagePercent - preliminaryDamagePercent).toFixed(2)}%
                    </td>
                  </tr>
                  <tr className="font-medium">
                    <td className="py-1">Compensation (kg)</td>
                    <td className="text-right">{preliminaryCompensation.toFixed(2)}</td>
                    <td className="text-right">{finalCompensation.toFixed(2)}</td>
                    <td className={`text-right ${Math.abs(finalCompensation - preliminaryCompensation) > 0.01 ? 'text-amber-600' : ''}`}>
                      {(finalCompensation - preliminaryCompensation).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reconciliation Alert */}
        {hasReconciliationNeeded && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
              <AlertTriangle className="h-5 w-5" />
              Reconciliation May Be Required
            </div>
            <p className="text-amber-600 text-sm">
              The final grading compensation ({finalCompensation.toFixed(2)} kg) differs from the 
              preliminary assessment ({preliminaryCompensation.toFixed(2)} kg) by{' '}
              <strong>{Math.abs(finalCompensation - preliminaryCompensation).toFixed(2)} kg</strong>.
              Please verify the payment amount reflects the correct compensation.
            </p>
          </div>
        )}

        {/* Expected Final Grading Date */}
        {isPreliminary && !isFinalComplete && quality.final_grading_expected_date && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            Final grading expected: {format(new Date(quality.final_grading_expected_date), 'PPP')}
          </div>
        )}

        {/* Grading Timestamps */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          {quality.preliminary_graded_at && (
            <span>Preliminary: {format(new Date(quality.preliminary_graded_at), 'PPP')}</span>
          )}
          {quality.final_graded_at && (
            <span>Final: {format(new Date(quality.final_graded_at), 'PPP')}</span>
          )}
        </div>

        {/* Notes */}
        {(quality.damage_notes || quality.preliminary_notes) && (
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm font-medium mb-1">Inspection Notes</p>
            <p className="text-sm text-muted-foreground">
              {quality.damage_notes || quality.preliminary_notes}
            </p>
          </div>
        )}

        {/* Per-Item Quality Breakdown for multi-item procurements */}
        {procurement.items && (procurement.items as ProcurementItem[]).length > 0 && (
          <ItemQualityInspectionSection 
            items={procurement.items as ProcurementItem[]}
            isPreliminary={isPreliminary}
            isFinalComplete={isFinalComplete}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default QualityInspectionSection;
